import { useSession } from '@/auth/useSession';
import { useTimeZone } from '@/auth/useTimeZone';
import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputField } from '@/components/forms/form-input-field';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { comboBoxOptionsOf } from '@/components/ui/multi-select-utils';
import { getMeCoach, patchCompleteCoachOnboardingStep, updateMeCoach } from '@/lib/api-calls';
import { isCoachOnboarding } from '@/lib/coach-utils';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { validateRouteRole } from '@/lib/routing';
import { AllTimeZones, getTimezoneTypeahead, timezoneZodSchema } from '@/lib/time-zones';
import { formatInstant, FrequentTimeZones, isCoachAllStatuses } from '@airreading/common';
import type { CoachOnboardingStep, GetMeCoachResult, UpdateMeCoachOptions } from '@airreading/common/data';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { CircleCheck, ClipboardPen, FlaskConical, Folder, Laptop, LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

const basicInformationSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  phone: z.string().trim().min(1, 'Phone number is required'),
  displayName: z.string().trim().min(1, 'Display name for students is required'),
  timeZone: timezoneZodSchema,
});

type BasicInformationForm = z.infer<typeof basicInformationSchema>;

const ONBOARDING_DOCUMENTS_URL = 'https://airtable.com/appGLIl83GeITBfQn/pagaxDt7ZQC1veySB/form';
const ONBOARDING_WORKSHOP_CALENDAR_URL = 'https://luma.com/airsetupws';
const ONBOARDING_TECH_CHECK_CALENDAR_URL = 'https://luma.com/airtechcheck';

/** Responsive task grid: ≥282px min column, auto-fit (3 cols ~1280px, wraps to 2 then 1). Inline avoids Tailwind arbitrary commas breaking the class. */
const onboardingTaskCardsGridStyle = {
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 282px), 1fr))',
};

function formatLastUploadedAt(instant: Temporal.Instant, timeZone: string | null | undefined): string {
  const tz = timeZone && timeZone.length > 0 ? timeZone : FrequentTimeZones.UTC;
  const { formattedDateWithoutYear, formattedTime } = formatInstant(instant, tz);
  return `${formattedDateWithoutYear} ${formattedTime}`;
}

function OnboardingCompleteFooter() {
  return (
    <div className="flex items-center gap-1 text-emerald-700 font-semibold">
      <CircleCheck className="h-5 w-5 shrink-0 fill-emerald-600 text-white" aria-hidden />
      <span>Complete</span>
    </div>
  );
}

export const Route = createFileRoute('/_layout/getting-ready-to-teach/')({
  beforeLoad: validateRouteRole(isCoachAllStatuses),
  component: GettingReadyPage,
});

function GettingReadyPage() {
  const { actingUser } = useSession();
  const isOnboarding = isCoachOnboarding(actingUser);

  if (!isOnboarding) {
    return <Navigate to="/my-classes/calendar" replace />;
  }

  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load your onboarding tasks. Please try again.',
          back: {
            backLink: {
              to: '/',
            },
          },
        };
      }}
    >
      <GettingReadyContent />
    </InfoPageSuspenseBoundary>
  );
}

function GettingReadyContent() {
  const { data: profile } = useSuspenseQuery(getMeCoach());
  const basicInformationCompleted = isBasicInformationCompleted(profile);
  const completedTaskCount =
    Number(basicInformationCompleted) +
    Number(profile.documentsReviewPassed) +
    Number(profile.workshopPassed) +
    Number(profile.onboardingTechCheckPassed);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutateAsync: completeOnboardingStep, isPending: isCompletingStep } = useMutation({
    mutationFn: patchCompleteCoachOnboardingStep,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: getMeCoach().queryKey });
    },
  });

  const runCompleteStep = async (step: CoachOnboardingStep) => {
    try {
      await completeOnboardingStep({ step });
    } catch (error: unknown) {
      toast({
        title: airServerErrorFrom(error)?.userFacingMessage || 'Could not update your progress. Please try again.',
      });
    }
  };

  const onExternalStepClick = async (step: CoachOnboardingStep, url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    await runCompleteStep(step);
  };

  return (
    <div className="w-full p-4 space-y-4">
      <div className="rounded-md border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 min-[900px]:flex-row min-[900px]:items-start min-[900px]:justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-2xl leading-none" aria-hidden>
                📖
              </span>
              <h2 className="text-2xl font-bold text-air-primary">Getting ready to teach with Air Reading</h2>
            </div>
            <p className="max-w-[72ch] text-sm leading-relaxed text-foreground">
              All tasks need to be completed before your first student group assignment. These tasks do not need to be
              completed in a specific order.
            </p>
          </div>
          <div className="min-[900px]:text-right">
            <div className="text-3xl font-bold tabular-nums text-air-primary">{completedTaskCount}/4</div>
            <div className="text-sm font-medium text-foreground">Progress</div>
          </div>
        </div>
      </div>

      <BasicInformationCard profile={profile} />

      <div className="grid w-full gap-4" style={onboardingTaskCardsGridStyle}>
        <OnboardingTaskCard
          icon={Folder}
          completed={profile.documentsReviewPassed}
          incompleteTitle="Upload documents"
          completeTitle="Documents verified"
          description={<p>Please upload your documents. We will review your submission within 1-2 business days.</p>}
          lastUploadedLine={
            !profile.documentsReviewPassed && profile.documentsLastUploadedAtInstant ? (
              <p className="text-sm text-foreground">
                Last uploaded at {formatLastUploadedAt(profile.documentsLastUploadedAtInstant, profile.timeZone)}
              </p>
            ) : null
          }
          actionLabel="Upload"
          actionDisabled={isCompletingStep}
          onActionClick={() => onExternalStepClick('documents', ONBOARDING_DOCUMENTS_URL)}
        />
        <OnboardingTaskCard
          icon={FlaskConical}
          completed={profile.workshopPassed}
          incompleteTitle="Getting ready to teach with Air Reading"
          completeTitle="Orientation workshop completed"
          description={
            <p>
              Getting ready to teach with Air Reading will walk you through the Air Reading curriculum and some best
              teaching practices.
              <br />
              <br />
              Visit the link above to view the schedule. If you can attend, please RSVP, if you cannot please revisit
              the page in a few days to see the updated schedule and find one that works for you.
            </p>
          }
          actionLabel="Schedule"
          actionDisabled={isCompletingStep}
          onActionClick={() => onExternalStepClick('workshop', ONBOARDING_WORKSHOP_CALENDAR_URL)}
        />
        <OnboardingTaskCard
          icon={Laptop}
          completed={profile.onboardingTechCheckPassed}
          incompleteTitle="Schedule a tech check"
          completeTitle="Tech check passed"
          description={
            <div className="space-y-2">
              <p>Please schedule a tech check with the Air Reading teacher quality team.</p>
              <div>
                <p>Pre-tech check must complete:</p>
                <ul className="list-disc pl-6">
                  <li>
                    Share the tech check slides in Zoom using advanced screen sharing mode and demonstrate your ability
                    to annotate on them.
                  </li>
                  <li>
                    Share the tech check slides in Google Meet and show how to advance through them and click on them.
                  </li>
                </ul>
              </div>
              <p>
                The tech check slide deck link is here:{' '}
                <a
                  href="https://tinyurl.com/45t5a7ps"
                  className="text-blue-600 underline hover:text-blue-800"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  https://tinyurl.com/45t5a7ps
                </a>
              </p>
            </div>
          }
          actionLabel="Schedule"
          actionDisabled={isCompletingStep}
          onActionClick={() => onExternalStepClick('techCheck', ONBOARDING_TECH_CHECK_CALENDAR_URL)}
        />
      </div>
    </div>
  );
}

function BasicInformationCard({ profile }: { profile: GetMeCoachResult }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { setTimezone } = useTimeZone();
  const { mutateAsync } = useMutation({ mutationFn: updateMeCoach });
  const basicInformationCompleted = isBasicInformationCompleted(profile);

  const onSubmit = ({ formData, disableEditing }: SubmitProps<BasicInformationForm>) => {
    const payload: UpdateMeCoachOptions = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      displayName: formData.displayName,
      timeZone: formData.timeZone.value,
      smsReminderOptedIn: profile.smsReminderOptedIn,
      secondaryEmail: profile.secondaryEmail || '',
      currentTimeZone: profile.timeZone ?? Temporal.Now.timeZoneId(),
      teachingPreferences: profile.teachingPreferences || [],
    };

    return mutateAsync(payload)
      .then((updatedProfile) => {
        if (updatedProfile.timeZone) {
          setTimezone(updatedProfile.timeZone);
        }
        toast({ title: 'Basic information updated' });
        disableEditing();
        queryClient.invalidateQueries({ queryKey: getMeCoach().queryKey });
      })
      .catch((error: unknown) => {
        toast({
          title: airServerErrorFrom(error)?.userFacingMessage || 'Unable to update your basic information.',
        });
      });
  };

  return (
    <EditableInfoCard
      headerText={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-air-primary/10">
            <ClipboardPen className="h-[26.4px] w-[26.4px] text-air-primary" />
          </div>
          <span>{basicInformationCompleted ? 'Basic information completed' : 'Basic information'}</span>
          {!basicInformationCompleted && (
            <span className="text-sm font-normal text-muted-foreground">
              (Please complete all your basic information)
            </span>
          )}
        </div>
      }
      formSchema={basicInformationSchema}
      onSubmit={onSubmit}
      showRedDot={!basicInformationCompleted}
      maxItemsPerRow={1}
      contentClass="grid-cols-1"
      defaultValues={{
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phone: profile.phone || '',
        displayName: profile.displayName || '',
        timeZone: {
          value: profile.timeZone || '',
          label: AllTimeZones.find((timeZone) => timeZone.value === profile.timeZone)?.label || '',
        },
      }}
      editButtonVariant="outline"
      saveButtonVariant="default"
      cancelButtonVariant="outline"
      footerActions={basicInformationCompleted ? <OnboardingCompleteFooter /> : undefined}
      data-testid="getting-ready-basic-info"
    >
      {({ form, editing }) => (
        <div className="grid grid-cols-1 gap-5 min-[640px]:grid-cols-2 min-[1024px]:grid-cols-3">
          <InfoKeyValue
            label={
              <>
                First name<span className="text-destructive">*</span>
              </>
            }
            value={profile.firstName || '-'}
            edit={{
              editing,
              editModeComponent: (
                <FormInputField
                  className="w-full"
                  form={form}
                  name="firstName"
                  label="First name"
                  inputProps={{ className: 'w-full', required: true }}
                />
              ),
            }}
          />
          <InfoKeyValue
            label={
              <>
                Last name<span className="text-destructive">*</span>
              </>
            }
            value={profile.lastName || '-'}
            edit={{
              editing,
              editModeComponent: (
                <FormInputField
                  className="w-full"
                  form={form}
                  name="lastName"
                  label="Last name"
                  inputProps={{ className: 'w-full', required: true }}
                />
              ),
            }}
          />
          <InfoKeyValue
            label={
              <>
                Phone number<span className="text-destructive">*</span>
              </>
            }
            value={profile.phone || '-'}
            edit={{
              editing,
              editModeComponent: (
                <FormInputField
                  className="w-full"
                  form={form}
                  name="phone"
                  label="Phone number"
                  inputProps={{ className: 'w-full', required: true }}
                />
              ),
            }}
          />
          <InfoKeyValue
            label={
              <>
                Display name for students<span className="text-destructive">*</span>
              </>
            }
            value={profile.displayName || '-'}
            edit={{
              editing,
              editModeComponent: (
                <FormInputField
                  className="w-full"
                  form={form}
                  name="displayName"
                  label="Display name for students"
                  inputProps={{ className: 'w-full', required: true }}
                />
              ),
            }}
          />
          <InfoKeyValue
            label={
              <>
                Time zone<span className="text-destructive">*</span>
              </>
            }
            value={profile.timeZone || '-'}
            edit={{
              editing,
              editModeComponent: (
                <FormComboBox
                  className="w-full"
                  form={form}
                  name="timeZone"
                  label="Time zone"
                  {...comboBoxOptionsOf(getTimezoneTypeahead)}
                  triggerProps={{ className: 'w-full' }}
                  required
                />
              ),
            }}
          />
        </div>
      )}
    </EditableInfoCard>
  );
}

function OnboardingTaskCard({
  icon: Icon,
  completed,
  incompleteTitle,
  completeTitle,
  description,
  lastUploadedLine,
  actionLabel,
  onActionClick,
  actionDisabled,
}: {
  icon: LucideIcon;
  completed: boolean;
  incompleteTitle: string;
  completeTitle: string;
  description: ReactNode;
  lastUploadedLine?: ReactNode;
  actionLabel: string;
  onActionClick: () => void | Promise<void>;
  actionDisabled?: boolean;
}) {
  return (
    <div
      className="@container min-w-0 rounded-md border bg-white p-4 shadow-sm flex min-h-[380px] flex-col"
      data-testid="getting-ready-task-card"
    >
      <div className="flex flex-1 min-h-0 flex-row items-start gap-3 @max-[640px]:flex-col">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-air-primary/10">
          <Icon className="h-5 w-5 text-air-primary" />
        </div>
        <div className="min-w-0 flex-1 flex flex-col gap-2">
          <h3 className="text-xl font-bold leading-tight text-foreground">
            {completed ? completeTitle : incompleteTitle}
          </h3>
          <div className="text-sm leading-relaxed text-foreground">{description}</div>
          {lastUploadedLine}
        </div>
      </div>
      <div className="mt-auto flex flex-wrap items-end justify-end gap-2 pt-4">
        {completed ? (
          <OnboardingCompleteFooter />
        ) : (
          <Button
            type="button"
            size="sm"
            variant="default"
            className="bg-[#EB404A] text-white hover:bg-[#EB404A]/90 focus-visible:ring-[#EB404A]"
            disabled={actionDisabled}
            onClick={() => void onActionClick()}
          >
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

function isBasicInformationCompleted(profile: GetMeCoachResult) {
  return [profile.firstName, profile.lastName, profile.phone, profile.displayName, profile.timeZone].every(
    (value) => typeof value === 'string' && value.trim().length > 0,
  );
}
